/**
 * Loads every page of the Signals list while a grouping needs the whole list
 * (a client group level — Local time / Business hours — or Type —
 * `groupingNeedsEveryPage`, `rankerGroupOrder.ts`, qa-batch4-plan.md D-C).
 * fix-plan-batch3.md D-A.
 *
 * Why the guard is `isFetching`, not `isFetchingNextPage` (F1, §1.1): with
 * `staleTime: Infinity` and no window-focus refetch the only page-1 refetches
 * are invalidations — and the tab invalidates itself (`useRefreshOnMount` →
 * `invalidateSignalQueries`) a moment after landing. A
 * `fetchNextPage({ cancelRefetch: false })` issued while that refetch is in
 * flight JOINS it (TanStack `Query.fetch` returns the in-flight retryer's
 * promise), so it resolves with no page added. `isFetchingNextPage` never
 * flips during it (it requires `fetchMeta.fetchMore.direction === 'forward'`,
 * which only a real next-page fetch sets), and `pagesLoaded` / `hasNextPage`
 * are unchanged when it settles — so an effect keyed on those never re-runs
 * and the list stalls one page short. `isFetching` is the only flag that
 * flips when the joined refetch settles: pausing on ANY fetch and depending
 * on it resumes the loop exactly then.
 *
 * Storm-proof: a failed page stops the loop (`isFetchNextPageError` —
 * `hasNextPage` stays true on a failing page), and at `maxPages` the normal
 * Load more takes over. StrictMode-safe with no lifetime ref: the
 * double-invoked effect's second call joins the first's in-flight fetch —
 * `cancelRefetch: false` is what makes TanStack dedupe; its default (`true`)
 * would CANCEL the in-flight page and request it again.
 *
 * Its exit half, `releaseAutoLoadedPages` / `useReleaseAutoLoadedPages`, puts the shared entry back to one page (fix-plan-batch4.md D-A).
 */
import { useEffect, useState } from 'react';
import { hashKey, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { FlowSignalsResponse } from '@shared/flow/signal-view';

/** The auto-load ceiling: 20 pages = 10,000 rows. Past it the normal Load more takes over. */
export const SIGNALS_AUTO_LOAD_MAX_PAGES = 20;

/** The slice of a `useInfiniteQuery` result the auto-loader reads — structural, so a test hands it a plain object. */
export interface AutoLoadQuery {
  hasNextPage: boolean;
  /** ANY in-flight fetch — a page-1 refetch included (fix-plan-batch3.md §1.1). */
  isFetching: boolean;
  isFetchNextPageError: boolean;
  pagesLoaded: number;
  fetchNextPage: (opts: { cancelRefetch: boolean }) => unknown;
}

export function useAutoLoadAllPages(enabled: boolean, query: AutoLoadQuery, maxPages = SIGNALS_AUTO_LOAD_MAX_PAGES): void {
  const { hasNextPage, isFetching, isFetchNextPageError, pagesLoaded, fetchNextPage } = query;
  useEffect(() => {
    if (!enabled || !hasNextPage || isFetching || isFetchNextPageError) return;
    if (pagesLoaded >= maxPages) return;
    void fetchNextPage({ cancelRefetch: false });
  }, [enabled, hasNextPage, isFetching, isFetchNextPageError, pagesLoaded, maxPages, fetchNextPage]);
}

/** Puts an entry the auto-loader filled back to its first page — the exit half of every-page mode
 *  (fix-plan-batch4.md D-A; QA checklist 74: "switch back to Assignee → Step: 500 loaded and Load more
 *  return"). Client levels are not in the key, so the entry is shared with the non-every-page grouping
 *  and would otherwise keep every page. Order matters and is verified against TanStack 5.90:
 *  1. cancel any in-flight fetch FIRST — infiniteQueryBehavior captures `oldPages` at fetch start and a
 *     late settle writes them all back over the trim; `cancelQueries` reverts synchronously
 *     (retryer.cancel → onCancel → setState(revertState)).
 *  2. read `isInvalidated` BEFORE trimming — a manual `setQueryData` clears it.
 *  3. trim to one page.
 *  4. if the cancelled fetch was the list's own invalidation refetch (useRefreshOnMount → invalidateSignalQueries),
 *     re-invalidate so page 1 refetches (active entry) or stays flagged (inactive one) — the Gong
 *     live-check's result must not be lost. Idempotent: a second call finds nothing to do. */
export function releaseAutoLoadedPages(queryClient: QueryClient, queryKey: QueryKey): void {
  if (queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching') {
    void queryClient.cancelQueries({ queryKey, exact: true });
  }
  const state = queryClient.getQueryState<InfiniteData<FlowSignalsResponse>>(queryKey);
  if (!state?.data || state.data.pages.length <= 1) return;
  const wasInvalidated = state.isInvalidated;
  queryClient.setQueryData<InfiniteData<FlowSignalsResponse>>(queryKey, old =>
    old && { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) });
  if (wasInvalidated) void queryClient.invalidateQueries({ queryKey, exact: true });
}

/** Calls `releaseAutoLoadedPages` on the entry the auto-loader was filling whenever it stops filling it:
 *  every-page mode ends (Local time / Business hours / Type removed) OR the key changes while it is on
 *  (Local time + Assignee → Local time + Type leaves the Assignee entry). Transition detection is
 *  React state, not a lifetime ref (CLAUDE.md StrictMode rule): a StrictMode double-invoked effect
 *  sees the same `prevActive` twice and calls the idempotent release twice. NOT an effect cleanup —
 *  a cleanup also runs on unmount and on StrictMode's simulated unmount, where it would cancel the
 *  auto-loader's first page-2 request and issue a second (SignalsFlowTable.test.tsx "StrictMode double
 *  effects issue exactly one page-2 request"). */
export function useReleaseAutoLoadedPages(enabled: boolean, queryKey: QueryKey): void {
  const queryClient = useQueryClient();
  const activeKey = enabled ? queryKey : null;
  const [prevActive, setPrevActive] = useState<QueryKey | null>(activeKey);
  useEffect(() => {
    const same = prevActive === activeKey
      || (prevActive !== null && activeKey !== null && hashKey(prevActive) === hashKey(activeKey));
    if (same) return;
    setPrevActive(activeKey);
    if (prevActive !== null) releaseAutoLoadedPages(queryClient, prevActive);
  }, [activeKey, prevActive, queryClient]);
}
